export const frontmatter = {
  title: "Реестр видов: что считается грибом",
  slug: "species-registry",
  category: "Источники данных",
  reading_minutes: 4,
  updated: "2026-04-29",
  abstract: "18 видов в замороженном словаре: что хранится, как привязано к лесу и почему мы НЕ показываем «вероятность съедобности».",
};

import { Callout, KeyFacts, Stats, Steps, TLDR } from "../../components/mdx";

# Реестр видов: что считается грибом

<TLDR>
Замороженный словарь из **18 видов грибов** + 3 ягод. Это контракт: на нём держится ML-классификация фото, матрица сродства с породами деревьев, страницы каталога. Менять можно только в одну сторону — добавлять, не удалять.
</TLDR>

<Stats stats={[
  { number: "18", label: "видов грибов", hint: "+ 3 ягоды" },
  { number: "9", label: "типов леса", hint: "матрица affinity" },
  { number: "5", label: "классов съедобности", hint: "только дискретно" },
  { number: "0", label: "процентов вероятности", hint: "не показываем" },
]} />

<KeyFacts title="Поля таблицы species" facts={[
  { label: "slug", value: <><code>boletus-edulis</code> — стабильный ASCII-идентификатор, не меняется</> },
  { label: "name_ru / name_lat", value: "русское и латинское название + synonyms для поиска" },
  { label: "edibility", value: <><code>edible / conditionally_edible / inedible / toxic / deadly</code></> },
  { label: "season_months", value: <>Массив месяцев: <code>[7, 8, 9, 10]</code> для белого</> },
  { label: "description", value: "короткое прозрачное описание для страницы вида" },
  { label: "photo_url / wiki_url", value: "Wikimedia Commons + Wikipedia" },
  { label: "red_book", value: "флаг краснокнижности на территории РФ" },
]} />

## Сродство к лесу

Отдельная таблица `species_forest_affinity(species_id, forest_type, affinity)` — матрица «насколько вид характерен для леса данного типа», 0..1. Типы леса — фиксированный словарь: `pine`, `spruce`, `birch`, `aspen`, `oak`, `mixed_coniferous`, `mixed_broadleaved`, `mixed`, `unknown`.

Например, белый гриб (`boletus-edulis`):

<KeyFacts facts={[
  { label: "spruce", value: "0.9 — еловая форма" },
  { label: "oak", value: "0.7 — дубовая" },
  { label: "birch", value: "0.5 — берёзовая" },
  { label: "pine", value: "0.3 — на сосне реже, чаще pine_bolete" },
]} />

<Callout type="info" title="Это экспертно-curated, не обучено">
Значения лежат в `db/seeds/species_registry.yaml`. Это сводка из ботанической литературы, которую можно править вручную при обнаружении ошибки. Endpoint `/api/forest/at` отдаёт «теоретические» виды — те, у кого affinity для доминирующей породы выше порога 0.4.
</Callout>

## Группировка для классификатора

VK-классификатор оперирует не slug'ами, а более крупными «группами»: qwen3.5-vl визуально не различает Leccinum aurantiacum и L. versipelle, но человек уверенно отличает «болет с оранжевой шляпкой» от «болета с серо-коричневой».

| Ключ из промта | Slug'и в species |
| --- | --- |
| `porcini` | `boletus-edulis` |
| `pine_bolete` | `boletus-edulis` (тот же slug — разделение только для статистики) |
| `aspen_bolete` | `leccinum-aurantiacum`, `leccinum-versipelle` |
| `birch_bolete` | `leccinum-scabrum` |
| `mokhovik` | `xerocomus-subtomentosus` |
| `chanterelle` | `cantharellus-cibarius`, `craterellus-tubaeformis` |
| `saffron_milkcap` | `lactarius-deliciosus` |
| `white_milkcap` | `lactarius-resimus` |
| `woolly_milkcap` | `lactarius-torminosus` |
| `spring_mushroom` | `morchella-esculenta`, `verpa-bohemica`, `gyromitra-esculenta` |
| `honey_fungus` | `armillaria-mellea`, `kuehneromyces-mutabilis` |
| `oyster` | `pleurotus-ostreatus` |
| `russula` | `russula-vesca` |
| `fly_agaric` | `amanita-muscaria` |

Различие между «porcini» и «pine_bolete» хранится в JSONB-поле `vk_post.photo_species` для аналитики по тонам шляпок, но в финальном агрегате по виду они объединены.

## Чего мы намеренно НЕ показываем

<Callout type="warn" title="Числовую вероятность съедобности">
Только дискретный класс из пяти. Любая цифра «съедобность 87%» — это либо средняя по больнице (бесполезно), либо ложная уверенность (опасно). Если вид условно-съедобный — пользователь обязан прочитать описание.
</Callout>

<Callout type="warn" title="Региональные вариации сезона">
`season_months` глобальное; в реальности белый под Лугой и под Тихвином может пойти с разницей в неделю-две. Поправка появится с прогноз-моделью (Phase 3+) — пока на это не претендуем.
</Callout>

<Callout type="warn" title="Конкретные «лучшие места» в публичном виде">
Показываем районы, где вид чаще всего фигурирует в VK-постах — но не геокоординаты конкретных полян. Куда внутри района — собственная разведка пользователя.
</Callout>

## Расширение реестра

<Steps steps={[
  {
    title: "Добавить запись в seed",
    body: <>В <code>db/seeds/species_registry.yaml</code>: slug, name_ru, name_lat, edibility, season_months, description, forest_affinities.</>,
  },
  {
    title: "Прогнать make db-seed",
    body: "UPSERT по slug, ничего не ломает.",
  },
  {
    title: "Если попадает в VK-классификатор",
    body: <>Добавить ключ в <code>CLASSIFY_SCHEMA</code> enum в <code>pipelines/ingest_vk.py</code>, описать диагностические признаки в текущем <code>vk_classify_v&#123;N&#125;.txt</code>, бампнуть <code>PHOTO_PROMPT_VERSION</code>. Photos-stage перезапустится.</>,
  },
  {
    title: "Подтянуть фото",
    body: <>Wikimedia Commons URL в <code>species.photo_url</code> (Phase 2 будет автоматизировано).</>,
  },
]} />
